// Stock Takes Home - Purpose-Driven Entry Point
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardList,
  Target,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Info,
  Zap,
  MapPin,
  BarChart3,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";

export function StockTakesHomePage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("stock-takes-home");

  const countOptions = [
    {
      id: "full-stock-take",
      icon: ClipboardList,
      title: "Full Stock Take",
      description: "Count all items in your entire inventory",
      badge: "Complete Count",
      badgeVariant: "default" as const,
      features: [
        "All items automatically included",
        "Perfect for year-end audits",
        "Complete inventory verification",
        "Compliance-ready reports",
      ],
      action: () => navigateTo("stock-takes/full/setup"),
      color: "blue",
    },
    {
      id: "targeted-count",
      icon: Target,
      title: "Targeted Count",
      description: "Count specific items, categories, or locations",
      badge: "Most Popular",
      badgeVariant: "secondary" as const,
      features: [
        "Choose specific items or categories",
        "Filter by location, value, or supplier",
        "Perfect for spot checks and location audits",
        "Flexible and customizable",
      ],
      action: () => navigateTo("stock-takes/targeted/setup"),
      color: "green",
    },
    {
      id: "cycle-count",
      icon: BarChart3,
      title: "Cycle Count",
      description: "ABC-based continuous counting or manual cycle count",
      badge: "Automated",
      badgeVariant: "outline" as const,
      features: [
        "ABC-based automatic scheduling",
        "High-value items counted more often",
        "Manual cycle count option",
        "Continuous inventory accuracy",
      ],
      action: () => navigateTo("stock-takes/cycle-count/setup"),
      color: "orange",
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return {
          border: "border-blue-200 dark:border-blue-800",
          hover: "hover:border-blue-300 dark:hover:border-blue-700",
          icon: "text-blue-600 dark:text-blue-400",
          iconBg: "bg-blue-50 dark:bg-blue-900/20",
        };
      case "green":
        return {
          border: "border-green-200 dark:border-green-800",
          hover: "hover:border-green-300 dark:hover:border-green-700",
          icon: "text-green-600 dark:text-green-400",
          iconBg: "bg-green-50 dark:bg-green-900/20",
        };
      case "purple":
        return {
          border: "border-purple-200 dark:border-purple-800",
          hover: "hover:border-purple-300 dark:hover:border-purple-700",
          icon: "text-purple-600 dark:text-purple-400",
          iconBg: "bg-purple-50 dark:bg-purple-900/20",
        };
      case "orange":
        return {
          border: "border-orange-200 dark:border-orange-800",
          hover: "hover:border-orange-300 dark:hover:border-orange-700",
          icon: "text-orange-600 dark:text-orange-400",
          iconBg: "bg-orange-50 dark:bg-orange-900/20",
        };
      default:
        return {
          border: "border-gray-200 dark:border-gray-800",
          hover: "hover:border-gray-300 dark:hover:border-gray-700",
          icon: "text-gray-600 dark:text-gray-400",
          iconBg: "bg-gray-50 dark:bg-gray-900/20",
        };
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo("stock-takes")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Stock Takes
          </Button>
        </div>
        <HelpButton onClick={toggleHelp} />
      </div>

      {/* Title Section */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Create Stock Take</h1>
        <p className="text-muted-foreground mt-1">
          Choose how you'd like to count your inventory
        </p>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Mobile App Coming Soon
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Soon you'll be able to scan barcodes directly in the warehouse using our mobile app.
                All counting pages are being designed with mobile-first in mind.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Count Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {countOptions.map((option) => {
          const Icon = option.icon;
          const colors = getColorClasses(option.color);

          return (
            <Card
              key={option.id}
              className={`relative transition-all cursor-pointer ${colors.border} ${colors.hover}`}
              onClick={option.action}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <div className={`p-3 rounded-lg ${colors.iconBg}`}>
                    <Icon className={`h-6 w-6 ${colors.icon}`} />
                  </div>
                  <Badge variant={option.badgeVariant}>{option.badge}</Badge>
                </div>
                <CardTitle className="text-xl">{option.title}</CardTitle>
                <CardDescription className="text-base">
                  {option.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  {option.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full" variant="outline">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />
    </div>
  );
}
